import { useAuth } from "../context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Lock, Clock, LogIn, MessageCircle, RefreshCw, ShieldX } from "lucide-react";

const WA_ADMIN = "6285261925686";

// Account-based access gate. Uses the SAME account as login:
//   - not logged in  -> ask to login/register
//   - logged in, status active (or admin) -> unlocked
//   - logged in, pending/disabled -> waiting for admin approval
export default function AccessGate({ pageTitle, featureId, adminOnly, children }) {
  const { user, isAdmin, loading, checkAuth } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500 font-mono-tt text-sm" data-testid="access-gate-checking">
        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" /> Memeriksa akses…
      </div>
    );
  }

  if (!user && window.tiroDesktopApp) return <Navigate to="/login" replace />;
  if (isAdmin) return children;
  if (adminOnly) {
    return (
      <div className="p-8 text-center text-slate-400 font-mono-tt text-sm" data-testid="access-gate-admin-only">
        This feature is only for admin-approved accounts.
      </div>
    );
  }

  // License (annual fee) expired -> lock everything until the admin renews it.
  if (user && user.license_expired) {
    const renewMsg = `Halo Admin TRMD Performance, lisensi saya sudah habis (${user.license_expires_at || "-"}). Saya mau perpanjang. Nama: ${user?.name || "-"}, Email: ${user?.email || "-"}, No HP: ${user?.phone || "-"}`;
    return (
      <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-500/90 flex items-center gap-2"><Lock className="h-4 w-4" /> Lisensi Kedaluwarsa</div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">{pageTitle}</h1>
          <p className="text-slate-500 text-sm mt-1">Masa aktif lisensi tahunan Anda sudah berakhir.</p>
        </div>
        <div data-testid="access-gate-license-expired" className="max-w-xl mx-auto rounded-2xl border border-red-900/40 bg-[#0c1017] p-8 relative overflow-hidden">
          <div className="tt-glow absolute inset-0" />
          <div className="relative space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-xl bg-red-500/15 border border-red-700/40 flex items-center justify-center"><Clock className="h-6 w-6 text-red-400" /></div>
              <div>
                <h2 className="font-head font-bold text-2xl uppercase text-white">Lisensi Habis</h2>
                <p className="text-sm text-slate-500">Perpanjang untuk lanjut memakai fitur</p>
              </div>
            </div>
            <div className="rounded-lg p-4 flex items-start gap-3 bg-red-950/30 border border-red-800/40">
              <ShieldX className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-red-300">Lisensi berakhir pada {user.license_expires_at || "-"}</div>
                <p className="text-xs text-slate-400 mt-0.5">Akun <span className="text-slate-200">{user.name || user.email}</span> aktif, namun lisensi tahunan sudah kedaluwarsa. Hubungi admin untuk memperpanjang, lalu klik "Cek Status".</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button data-testid="license-refresh-btn" onClick={() => { checkAuth(); toast.info("Status diperbarui"); }} variant="outline"
                className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Cek Status
              </Button>
            </div>
            <a href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(renewMsg)}`} target="_blank" rel="noreferrer" data-testid="license-renew-whatsapp-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-bold transition-colors">
              <MessageCircle className="h-5 w-5" /> Perpanjang Lisensi (WhatsApp)
            </a>
          </div>
        </div>
      </div>
    );
  }

  const featsOk = !featureId || !Array.isArray(user?.features) || user.features.includes(featureId);
  if (user && user.status === "active" && featsOk) return children;
  const featureLocked = user && user.status === "active" && !featsOk;

  const waLink = () => {
    const msg = `Halo Admin TRMD Performance, saya minta persetujuan akun untuk fitur "${pageTitle}". Nama: ${user?.name || "-"}, Email: ${user?.email || "-"}, No HP: ${user?.phone || "-"}`;
    return `https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-500/90 flex items-center gap-2"><Lock className="h-4 w-4" /> Restricted · Butuh Akun</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">{pageTitle}</h1>
        <p className="text-slate-500 text-sm mt-1">Fitur ini hanya untuk akun yang sudah disetujui admin.</p>
      </div>

      <div data-testid="access-gate-container" className="max-w-xl mx-auto rounded-2xl border border-red-900/40 bg-[#0c1017] p-8 relative overflow-hidden">
        <div className="tt-glow absolute inset-0" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-xl bg-red-500/15 border border-red-700/40 flex items-center justify-center"><Lock className="h-6 w-6 text-red-400" /></div>
            <div>
              <h2 className="font-head font-bold text-2xl uppercase text-white">{!user ? "Login Diperlukan" : featureLocked ? "Fitur Terkunci" : "Menunggu Persetujuan"}</h2>
              <p className="text-sm text-slate-500">Akses pakai akun Anda</p>
            </div>
          </div>

          {!user ? (
            <div className="space-y-4" data-testid="access-gate-login-required">
              <p className="text-sm text-slate-400">Silakan login atau daftar akun terlebih dahulu. Setelah admin menyetujui akun Anda, fitur ini otomatis terbuka.</p>
              <Button data-testid="access-gate-login-btn" onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-slate-950 font-bold">
                <LogIn className="h-4 w-4 mr-1.5" /> Login / Daftar
              </Button>
            </div>
          ) : featureLocked ? (
            <div className="space-y-4" data-testid="access-gate-feature-locked">
              <div className="rounded-lg p-4 flex items-start gap-3 bg-red-950/30 border border-red-800/40">
                <ShieldX className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-red-300">Fitur belum diaktifkan untuk akun Anda</div>
                  <p className="text-xs text-slate-400 mt-0.5">Akun <span className="text-slate-200">{user.name || user.email}</span> aktif, tapi fitur <span className="text-slate-200">"{pageTitle}"</span> belum diberikan admin. Hubungi admin untuk mengaktifkannya.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button data-testid="access-gate-refresh-btn" onClick={() => { checkAuth(); toast.info("Status diperbarui"); }} variant="outline"
                  className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Cek Ulang
                </Button>
              </div>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="access-gate-whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-bold transition-colors">
                <MessageCircle className="h-5 w-5" /> Minta Aktivasi (WhatsApp)
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              <div data-testid="access-gate-pending" className={`rounded-lg p-4 flex items-start gap-3 ${user.status === "disabled" || user.status === "rejected" ? "bg-red-950/30 border border-red-800/40" : "bg-amber-950/30 border border-amber-800/40"}`}>
                {user.status === "disabled" || user.status === "rejected"
                  ? <ShieldX className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  : <Clock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />}
                <div>
                  <div className={`font-bold ${user.status === "disabled" || user.status === "rejected" ? "text-red-300" : "text-amber-300"}`}>
                    {user.status === "disabled" ? "Akun dinonaktifkan" : user.status === "rejected" ? "Akun ditolak" : "Menunggu persetujuan admin"}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Akun <span className="text-slate-200">{user.name || user.email}</span> ({user.phone || "no HP -"}). Hubungi admin untuk mempercepat, lalu klik "Cek Status".</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button data-testid="access-gate-refresh-btn" onClick={() => { checkAuth(); toast.info("Status diperbarui"); }} variant="outline"
                  className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Cek Status
                </Button>
              </div>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="access-gate-whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-bold transition-colors">
                <MessageCircle className="h-5 w-5" /> Hubungi Admin (WhatsApp)
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
